import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";

// Device settings of the POS apps (tablette iPad/Android = pos_clone, Desktop =
// electron-pos). They live on each device; every device backs them up to
// /api/pos-config (clicoo PosConfigController, one row per device), which is
// what this screen reads.
//
// Edits never touch that backup: they are queued as versioned pending changes
// (PUT /pos-config/{id}/changes, clicoo App\Service\PosConfigChanges) that the
// tablet pulls on its sync timer, applies, acknowledges, then re-backs up
// (pos_clone ConfigBackupService.pullRemoteChanges). Desktop doesn't pull
// them yet: its settings stay read-only here.
//
// Payload format: electron-pos/docs/POS_CONFIG_PAYLOAD.md (schemaVersion 2).
// `settings.<section>` holds the device's raw storage values, so a value may be
// a JSON string, a boolean, or 'true'/'false' depending on the app.

const headers = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account.id};
};

// Light list: `settings.<section>` = key names only (a Desktop backup with
// customer display images weighs several MB).
export const fetchPosConfigs = async () => {
    const {data} = await api.get("/pos-config", {headers: await headers(), params: {summary: 1}});
    return Array.isArray(data?.configs) ? data.configs : [];
};

export const fetchPosConfig = async (id) => {
    const {data} = await api.get("/pos-config", {headers: await headers(), params: {id}});
    return data?.config || null;
};

// body: {settings, removeKeys, printers: {upsert, delete}, baseVersion}
export const putPosConfigChanges = async (id, body) => {
    const {data} = await api.put(`/pos-config/${id}/changes`, body, {headers: await headers()});
    return data;
};

export const cancelPosConfigChanges = async (id) => {
    const {data} = await api.delete(`/pos-config/${id}/changes`, {headers: await headers()});
    return data;
};

// ─── Editable keys ───────────────────────────────────────────────────────────

// Same table as pos_clone services/ConfigBackupService.js REMOTE_EDITABLE_KEYS
// (the tablet ignores any other key), with the storage format the POS writes
// itself and the value the POS uses when the key was never saved:
//   boolString 'true' | 'false' · bool true | false · json object as a JSON
//   string · [..] one of these strings
export const EDITABLE_KEYS = {
    ticket_settings: {format: "json"},
    journal_settings: {format: "json"},
    kitchen_printer_config: {format: "json"},
    kitchen_printer_config_products: {format: "json"},
    kitchen_printer_config_menus: {format: "json"},
    kitchen_order_number_mode: {format: ["local", "cloud"], default: "local"},
    print_order_number_on_tickets: {format: "bool", default: false},
    default_search_mode: {format: ["barcode", "name"], default: "barcode"},
    camera_scanner_enabled: {format: "boolString", default: false},
    virtual_keyboard_mode: {format: ["auto", "manual"], default: "auto"},
    category_sort_order: {format: ["backoffice", "name_asc", "name_desc"], default: "backoffice"},
    catalogue_show_all: {format: "boolString", default: true},
    catalogue_show_brands: {format: "boolString", default: true},
    catalogue_show_packs: {format: "boolString", default: true},
    catalogue_show_recents: {format: "boolString", default: true},
    catalogue_two_line_categories: {format: "boolString", default: false},
    product_grid_columns: {format: ["auto", "5", "6", "7"], default: "auto"},
    product_grid_show_price: {format: "boolString", default: true},
    product_card_size: {format: ["small", "medium", "large"], default: "medium"},
    product_card_full_name: {format: "boolString", default: false},
    quickbar_show_channel: {format: "boolString", default: true},
    quickbar_show_client: {format: "boolString", default: true},
    quickbar_show_refund: {format: "boolString", default: true},
    quickbar_show_table: {format: "boolString", default: true},
    // Nom de la caisse (POS : Réglages > Mon matériel). Vide = sans nom :
    // envoyé en removeKeys (buildChangesBody), la tablette ignore "".
    register_name: {format: "text", default: ""},
};

export const canEditDevice = (config) => isMobile(config);

// Decoded value (bool / string / object) → the raw value the POS stores
export const encodeValue = (key, value) => {
    const {format} = EDITABLE_KEYS[key];
    if (format === "boolString") return String(!!value);
    if (format === "bool") return !!value;
    if (format === "json") return JSON.stringify(value || {});
    if (format === "text") return String(value ?? "").replace(/\s+/g, " ").trim().slice(0, 40);
    return String(value);
};

// Raw stored value → decoded value for an editor (POS default when absent)
export const decodeValue = (key, raw) => {
    const {format, default: fallback} = EDITABLE_KEYS[key];
    if (format === "json") {
        const defaults = key === "ticket_settings" ? TICKET_DEFAULTS : key === "journal_settings" ? JOURNAL_DEFAULTS : {};
        return {...defaults, ...(parseObject(raw) || {})};
    }
    if (format === "bool" || format === "boolString") return toBool(raw) ?? fallback;
    const value = parseValue(raw);
    return value === undefined || value === null || value === "" ? fallback : String(value);
};

// ─── Pending changes overlay ─────────────────────────────────────────────────

// The device's backup with the not-yet-applied backoffice changes laid over it,
// so the screen shows what the device will have once it syncs.
export const applyPendingChanges = (config) => {
    const pending = config?.pendingChanges;
    if (!pending) return config;

    const removed = new Set(pending.removeKeys || []);
    const changed = new Set([...Object.keys(pending.settings || {}), ...removed]);
    const settings = Object.fromEntries(Object.entries(config.settings || {}).map(([section, bag]) => [
        section,
        section === "employeePrefs" || !bag || typeof bag !== "object" || Array.isArray(bag)
            ? bag
            : Object.fromEntries(Object.entries(bag).filter(([key]) => !changed.has(key))),
    ]));
    settings.__pending = {...(pending.settings || {})};

    const upserts = new Map((pending.printers?.upsert || []).map(p => [p.id, p]));
    const deleted = new Set(pending.printers?.delete || []);
    const newPrimary = [...upserts.values()].find(p => p.active)?.id;
    const printers = (config.printers || [])
        .filter(p => !deleted.has(p.id))
        .map(p => ({...p, ...(upserts.get(p.id) || {})}))
        .concat([...upserts.values()].filter(p => !(config.printers || []).some(o => o.id === p.id)))
        .map(p => (newPrimary && p.id !== newPrimary ? {...p, active: false} : p));

    return {...config, settings, printers};
};

// ─── Raw value decoding ─────────────────────────────────────────────────────

export const parseValue = (value) => {
    if (typeof value !== "string") return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

export const parseObject = (value) => {
    const parsed = parseValue(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
};

// true / false, or undefined when the device never saved the key
export const toBool = (value) => {
    const parsed = parseValue(value);
    if (parsed === undefined || parsed === null || parsed === "") return undefined;
    return parsed === true || parsed === 1;
};

export const isMobile = (config) => ["ios", "android"].includes(config?.device?.platform);

export const PLATFORM_LABELS = {
    ios: "iPad / iOS",
    android: "Android",
    desktop: "Desktop",
};

// Every key of every section, merged — keys are unique across sections.
export const flattenSettings = (config) => {
    const bags = config?.settings || {};
    return Object.entries(bags).reduce((all, [section, bag]) => (
        section === "employeePrefs" || !bag || typeof bag !== "object" || Array.isArray(bag)
            ? all
            : {...all, ...bag}
    ), {});
};

// Connexion de l'appareil À CE COMPTE. lastSeenAt avance à chaque contact
// (la caisse interroge le serveur toutes les minutes) ; un appareil passé sur
// plusieurs comptes garde une sauvegarde par compte, et seule celle du compte
// où il est connecté avance. « away » = les modifications posées ici ne
// seront appliquées qu'à son retour sur ce compte.
export const ONLINE_WINDOW_MS = 3 * 60 * 1000;

export const deviceConnection = (config, now = Date.now()) => {
    const lastSeen = Number(config?.lastSeenAt) || 0;
    if (!lastSeen) return {state: "unknown", lastSeen: null};
    return {
        state: now - lastSeen <= ONLINE_WINDOW_MS ? "online" : "away",
        lastSeen: new Date(lastSeen),
    };
};

export const formatSince = (date, now = Date.now()) => {
    if (!date) return "";
    const minutes = Math.max(0, Math.round((now - date.getTime()) / 60000));
    if (minutes < 1) return "à l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.round(hours / 24);
    return `il y a ${days} jour${days > 1 ? "s" : ""}`;
};

export const lastBackupDate = (config) => {
    if (config?.pushedAt) return new Date(Number(config.pushedAt));
    return config?.updatedAt ? new Date(config.updatedAt) : null;
};

// ─── Ticket ──────────────────────────────────────────────────────────────────

// Same defaults as the tablet's Réglages > Ticket (pos_clone TicketSettings.js):
// the POS merges the saved object over them, so do we.
export const TICKET_DEFAULTS = {
    printCustomerReceipt: true,
    printKitchenTicket: true,
    openCashDrawer: false,
    printWorkflow: "receipt_first",
    reprintKitchenOnCheckout: true,
    printKitchenProductDescription: false,
    printTicketName: true,
    printTicketLogo: false,
    printItemNotes: false,
    printSaleNote: false,
    printItemOptions: true,
    printCustomerInfo: false,
    sendByEmail: true,
    sendByWhatsUp: true,
    showICE: true,
    showIF: true,
    showEmail: true,
    showPhone: true,
    showWebsite: true,
    showEmployeeName: true,
    showTVA: true,
    showTVADetail: false,
    showClientName: true,
    showClientPhone: true,
    showClientEmail: true,
    showClientAddress: true,
};

export const getTicketSettings = (config) => {
    const saved = parseObject(flattenSettings(config).ticket_settings) || parseObject(config?.ticket);
    return saved ? {...TICKET_DEFAULTS, ...saved} : null;
};

export const JOURNAL_DEFAULTS = {
    sendJournalByEmail: true,
    defaultEmail: "",
    printByCategories: true,
    emailByCategories: true,
    deletePendingSalesOnClose: true,
};

export const getJournalSettings = (config) => {
    const saved = parseObject(flattenSettings(config).journal_settings);
    return saved ? {...JOURNAL_DEFAULTS, ...saved} : null;
};

// ─── Kitchen routing ─────────────────────────────────────────────────────────

// pos_clone utilis/kitchenPrinterIds.js: "never print this on a kitchen ticket"
export const NO_KITCHEN_PRINT = "__no_kitchen_print__";

export const toPrinterIdList = (value) =>
    (Array.isArray(value) ? value : value ? [value] : []).filter(id => typeof id === "string" && id);

// Normalised kitchen routing of a device, whichever app wrote it:
//  - tablette: kitchen_printer_config = {categoryId: printerId | [printerIds]},
//    plus _products (product/variant overrides) and _menus
//    ({menuId: {default, items: {productOrVariantId: printerId}}});
//  - Desktop: {enabled, defaultPrinter, routing: {categoryId: printerId}}.
export const getKitchenRouting = (config) => {
    const flat = flattenSettings(config);
    const main = parseObject(flat.kitchen_printer_config) || {};
    const desktopShape = "routing" in main || "defaultPrinter" in main || "enabled" in main;
    const desktop = desktopShape ? main : parseObject(config?.kitchen);

    if (desktopShape || (!isMobile(config) && desktop)) {
        return {
            enabled: desktop?.enabled !== false,
            defaultPrinter: desktop?.defaultPrinter || "",
            categories: desktop?.routing || {},
            products: {},
            menus: {},
        };
    }
    return {
        enabled: true,
        defaultPrinter: "",
        categories: main,
        products: parseObject(flat.kitchen_printer_config_products) || {},
        menus: parseObject(flat.kitchen_printer_config_menus) || {},
    };
};

// ─── KDS ─────────────────────────────────────────────────────────────────────

// `flat` = flattenSettings(config). The connection secret is never shown.
export const getKdsConfig = (flat) => {
    const kds = parseObject(flat.local_kds_config);
    if (!kds) return null;
    return {enabled: !!kds.enabled, baseUrl: kds.baseUrl || kds.url || "", hasSecret: !!kds.secret};
};
